import React, { useEffect, useState, useRef } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import RichTextEditor from "@/components/common/RichTextEditor";
import MediaImageField from "@/components/common/MediaImageField";
import MediaVideoField from "@/components/common/MediaVideoField";
import SeoFields from "@/components/seo/SeoFields";
import SeoFormScore from "@/components/seo/SeoFormScore";
import SeoLivePreview from "@/components/seo/SeoLivePreview";
import { CRUD } from "@/constants/testIds";
import api from "@/lib/api";
import { mediaPublicUrl } from "@/lib/mediaUrl";
import { resolveMediaUrl } from "@/lib/serviceMedia";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function getPath(obj, path) {
  if (!path) return undefined;
  if (!path.includes(".")) return obj?.[path];
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function setPath(obj, path, value) {
  if (!path.includes(".")) return { ...obj, [path]: value };
  const keys = path.split(".");
  const root = { ...obj };
  let cur = root;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const k = keys[i];
    const next = cur[k];
    cur[k] = next && typeof next === "object" && !Array.isArray(next) ? { ...next } : {};
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return root;
}

function charHint(len, min, max) {
  if (min == null && max == null) return null;
  const ok = (min == null || len >= min) && (max == null || len <= max);
  const range = min != null && max != null ? `${min}–${max}` : max != null ? `up to ${max}` : `${min}+`;
  return { ok, text: `${len} characters · aim ${range}` };
}

/**
 * Generic CRUD drawer. `fields` is an array of:
 *   { key, label, type, options?, hint?, section?, minLen?, maxLen?, placeholder? }
 * Nested keys like `seo.meta_title` are supported.
 */
export default function CrudDrawer({ open, onOpenChange, title, fields, initial, onSave, resourceKey }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState("{}");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [fullView, setFullView] = useState(false);

  useEffect(() => {
    const next = initial || {};
    setValues(next);
    setBaseline(JSON.stringify(next));
  }, [initial, open]);

  useEffect(() => {
    if (!open) setFullView(false);
  }, [open]);

  const set = (k, v) => setValues((prev) => setPath(prev, k, v));
  const get = (k) => getPath(values, k);
  const dirty = JSON.stringify(values) !== baseline;

  const requestClose = (nextOpen) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (dirty && !saving) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onSave(values);
      setBaseline(JSON.stringify(values));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!saving) submit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, values]); // eslint-disable-line react-hooks/exhaustive-deps

  let lastSection = null;

  return (
    <Sheet open={open} onOpenChange={requestClose}>
      <SheetContent
        data-testid={CRUD.drawer(resourceKey)}
        className={cn(
          "glass-strong border-white/10 text-white p-0 flex flex-col transition-[width,max-width] duration-200",
          fullView
            ? "!w-[min(100vw-1rem,92rem)] !max-w-none sm:!max-w-none"
            : "w-full sm:!max-w-xl"
        )}
      >
        <SheetHeader className="px-6 pt-5 pb-3 text-left border-b border-white/[0.06] shrink-0">
          <div className="flex items-start justify-between gap-3 pr-8">
            <div className="min-w-0 space-y-1">
              <SheetTitle className="text-white">{title}</SheetTitle>
              <SheetDescription className="text-white/50">
                {dirty ? "Unsaved changes · Ctrl/⌘S to save" : "Fill in the details and save."}
              </SheetDescription>
            </div>
            <button
              type="button"
              onClick={() => setFullView((v) => !v)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/[0.04] px-2.5 py-1.5 text-xs text-white/75 hover:text-white hover:bg-white/10 hover:border-white/25 transition-colors"
              title={fullView ? "Collapse drawer" : "Expand drawer"}
              aria-label={fullView ? "Collapse drawer" : "Expand drawer"}
              aria-pressed={fullView}
            >
              {fullView ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{fullView ? "Collapse" : "Expand"}</span>
            </button>
          </div>
        </SheetHeader>
        <div className={`flex-1 overflow-y-auto px-6 py-4 ${fullView ? "grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4 content-start" : "space-y-4"}`}>
          {fields.map((f) => {
            const sectionBlock = f.section && f.section !== lastSection ? (
              <div key={`sec-${f.section}`} className={`pt-2 pb-1 border-t border-white/10 first:border-0 first:pt-0 ${fullView ? "lg:col-span-2" : ""}`}>
                <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-200/70 font-medium">{f.section}</p>
                {f.sectionHint && <p className="text-[11px] text-white/40 mt-1 leading-relaxed">{f.sectionHint}</p>}
              </div>
            ) : null;
            if (f.section) lastSection = f.section;

            const raw = get(f.key);
            const strVal = raw == null ? "" : String(raw);
            const hint = charHint(strVal.length, f.minLen, f.maxLen);

            return (
              <React.Fragment key={f.key}>
                {sectionBlock}
                <div className={`space-y-1.5 ${fullView && (f.type === "seo" || f.type === "richtext" || f.type === "gallery" || f.type === "sections" || f.type === "textarea") ? "lg:col-span-2" : ""}`}>
                  {f.type !== "seo" && (
                    <Label htmlFor={f.key} className="text-xs uppercase tracking-wider text-white/50">{f.label}</Label>
                  )}
                  {f.type === "richtext" ? (
                    <RichTextEditor
                      value={raw ?? ""}
                      onChange={(html) => set(f.key, html)}
                      placeholder={f.placeholder || "Start writing…"}
                      testId={`${resourceKey}-${f.key}`}
                    />
                  ) : f.type === "textarea" ? (
                    <Textarea
                      id={f.key}
                      value={raw ?? ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      rows={f.rows || (fullView ? 6 : 4)}
                      placeholder={f.placeholder}
                      className={`bg-black/20 text-white placeholder:text-white/30 ${hint && !hint.ok ? "border-amber-400/50" : "border-white/10"}`}
                    />
                  ) : f.type === "choice-row" ? (
                    <div className="flex flex-wrap gap-1.5">
                      {(f.options || []).map((o) => {
                        const on = (raw ?? "") === o.value;
                        return (
                          <button
                            key={o.value}
                            type="button"
                            onClick={() => set(f.key, o.value)}
                            className={`px-3 h-9 rounded-lg text-sm border transition-colors ${
                              on
                                ? "bg-white/12 text-white border-cyan-400/40"
                                : "text-white/55 border-white/10 hover:text-white hover:border-white/25"
                            }`}
                          >
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : f.type === "select" ? (
                    <Select value={raw || undefined} onValueChange={(v) => set(f.key, v)}>
                      <SelectTrigger className="bg-black/20 border-white/10 text-white">
                        <SelectValue placeholder={`Select ${f.label.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent className="glass-strong border-white/10">
                        {(() => {
                          const opts = [...(f.options || [])];
                          if (raw && !opts.some((o) => o.value === raw)) {
                            opts.unshift({ label: `${raw} (current)`, value: raw });
                          }
                          return opts.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                  ) : f.type === "switch" ? (
                    <div className="flex items-center gap-3">
                      <Switch
                        id={f.key}
                        checked={!!raw}
                        onCheckedChange={(v) => set(f.key, v)}
                      />
                      <span className="text-sm text-white/60">{raw ? "Enabled" : "Disabled"}</span>
                    </div>
                  ) : f.type === "tags" ? (
                    <Input
                      id={f.key}
                      value={Array.isArray(raw) ? raw.join(", ") : (raw ?? "")}
                      onChange={(e) => set(f.key, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                      placeholder={f.placeholder || "Comma-separated"}
                      className="bg-black/20 border-white/10 text-white placeholder:text-white/30"
                    />
                  ) : f.type === "number" ? (
                    <Input
                      id={f.key}
                      type="number"
                      value={raw ?? ""}
                      onChange={(e) => set(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                      className="bg-black/20 border-white/10 text-white"
                    />
                  ) : f.type === "image" ? (
                    <MediaImageField
                      value={raw ?? (f.valueMode === "media" ? null : "")}
                      onChange={(url) => set(f.key, url)}
                      folder={f.folder || "uploads"}
                      valueMode={f.valueMode || "url"}
                      hint={f.hint}
                      aspectClass={fullView ? "h-48" : "h-28"}
                    />
                  ) : f.type === "video" ? (
                    <MediaVideoField
                      value={raw ?? ""}
                      onChange={(url) => set(f.key, url)}
                      folder={f.folder || "blog"}
                      hint={f.hint}
                    />
                  ) : f.type === "seo" ? (
                    <div className="space-y-4">
                      <SeoFields
                        value={raw || {}}
                        onChange={(next) => set(f.key, next)}
                        ogTypeDefault={f.ogTypeDefault || "website"}
                        mediaFolder={f.folder || "uploads"}
                        compact
                      />
                      <SeoFormScore resource={resourceKey} values={values} />
                      <SeoLivePreview
                        path={typeof f.pathFrom === "function" ? f.pathFrom(values) : (f.path || "")}
                        draftSeo={raw || {}}
                        fallbackTitle={values.title || values.name || ""}
                        fallbackDescription={values.excerpt || values.description || ""}
                        fallbackImage={values.featured_image || values.cover_image || ""}
                      />
                    </div>
                  ) : f.type === "gallery" ? (
                    <GalleryUploadField
                      value={raw ?? []}
                      onChange={(urls) => set(f.key, urls)}
                      folder={f.folder || "blog"}
                    />
                  ) : f.type === "sections" ? (
                    <SectionsEditor
                      value={raw ?? []}
                      onChange={(sections) => set(f.key, sections)}
                      folder={f.folder || "blog"}
                    />
                  ) : (
                    <Input
                      id={f.key}
                      value={raw ?? ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className={`bg-black/20 text-white placeholder:text-white/30 ${hint && !hint.ok ? "border-amber-400/50" : "border-white/10"}`}
                    />
                  )}
                  {hint && (
                    <p className={`text-[11px] ${hint.ok ? "text-emerald-300/80" : "text-amber-200/80"}`}>{hint.text}</p>
                  )}
                  {f.hint && <p className="text-[11px] text-white/40 leading-relaxed">{f.hint}</p>}
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="shrink-0 px-6 py-4 border-t border-white/[0.06] flex items-center justify-between gap-2 glass-strong">
          <span className={`text-[11px] ${dirty ? "text-amber-300" : "text-white/35"}`}>{dirty ? "Unsaved" : "Saved"}</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => requestClose(false)} className="text-white/70 hover:text-white hover:bg-white/5">Cancel</Button>
            <Button
              data-testid={CRUD.saveBtn(resourceKey)}
              onClick={submit}
              disabled={saving}
              className="btn-gradient text-white border-0"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </SheetContent>
      {discardOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="glass-strong rounded-xl border border-white/10 p-5 max-w-sm w-full">
            <p className="text-white font-medium">Discard unsaved changes?</p>
            <p className="text-sm text-white/55 mt-1">Your edits in this drawer will be lost.</p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setDiscardOpen(false)} className="text-white/70 hover:text-white">Keep editing</Button>
              <Button onClick={() => { setDiscardOpen(false); onOpenChange(false); }} className="bg-red-600 hover:bg-red-500 text-white border-0">Discard</Button>
            </div>
          </div>
        </div>
      )}
    </Sheet>
  );
}


function GalleryUploadField({ value = [], onChange, folder = "blog" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder", folder);
        const { data } = await api.post("/media/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(mediaPublicUrl(data));
      }
      onChange([...value, ...newUrls]);
      toast.success(`${newUrls.length} image(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (e) => {
    upload(e.target.files);
    e.target.value = "";
  };

  const removeImage = (idx) => {
    const updated = value.filter((_, i) => i !== idx);
    onChange(updated);
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const arr = [...value];
    [arr[from], arr[to]] = [arr[to], arr[from]];
    onChange(arr);
  };

  return (
    <div className="space-y-3">
      {/* Preview grid */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {value.map((url, idx) => (
            <div key={idx} className="relative group rounded-lg overflow-hidden border border-white/10 aspect-square">
              <img src={resolveMediaUrl(url) || url} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {idx > 0 && (
                  <button type="button" onClick={() => moveImage(idx, idx - 1)} className="bg-white/20 hover:bg-white/30 text-white rounded w-6 h-6 text-xs">←</button>
                )}
                <button type="button" onClick={() => removeImage(idx)} className="bg-red-500/80 hover:bg-red-500 text-white rounded w-6 h-6 text-xs">✕</button>
                {idx < value.length - 1 && (
                  <button type="button" onClick={() => moveImage(idx, idx + 1)} className="bg-white/20 hover:bg-white/30 text-white rounded w-6 h-6 text-xs">→</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      <div
        className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-white/20 hover:border-white/40 transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
      >
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
        {uploading ? (
          <span className="text-sm text-white/50">Uploading...</span>
        ) : (
          <>
            <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="text-sm text-white/50">Add images ({value.length} uploaded)</span>
          </>
        )}
      </div>
    </div>
  );
}


function SectionsEditor({ value = [], onChange, folder = "blog" }) {
  const addSection = () => {
    onChange([...value, { title: "", content: "", image: "" }]);
  };

  const updateSection = (idx, field, val) => {
    const updated = value.map((s, i) => i === idx ? { ...s, [field]: val } : s);
    onChange(updated);
  };

  const removeSection = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const moveSection = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const arr = [...value];
    [arr[from], arr[to]] = [arr[to], arr[from]];
    onChange(arr);
  };

  return (
    <div className="space-y-3">
      {value.map((section, idx) => (
        <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-white/50">Section {idx + 1}</span>
            <div className="flex gap-1">
              {idx > 0 && (
                <button type="button" onClick={() => moveSection(idx, idx - 1)} className="text-white/40 hover:text-white text-xs px-1">↑</button>
              )}
              {idx < value.length - 1 && (
                <button type="button" onClick={() => moveSection(idx, idx + 1)} className="text-white/40 hover:text-white text-xs px-1">↓</button>
              )}
              <button type="button" onClick={() => removeSection(idx)} className="text-red-400 hover:text-red-300 text-xs px-1">✕</button>
            </div>
          </div>
          <Input
            value={section.title}
            onChange={(e) => updateSection(idx, "title", e.target.value)}
            placeholder="Section title (optional)"
            className="bg-black/20 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
          <Textarea
            value={section.content}
            onChange={(e) => updateSection(idx, "content", e.target.value)}
            placeholder="Section text..."
            rows={3}
            className="bg-black/20 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
          <MediaImageField
            value={section.image || ""}
            onChange={(url) => updateSection(idx, "image", url)}
            folder={folder}
            aspectClass="h-28"
          />
        </div>
      ))}

      <button
        type="button"
        onClick={addSection}
        className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-white/20 hover:border-white/40 text-white/50 hover:text-white/70 text-sm transition-colors"
      >
        + Add Section
      </button>
    </div>
  );
}
